import React, { useState, useEffect } from 'react';
import { IonModal, IonContent } from '@ionic/react';
import { createUseStyles } from 'react-jss';

const useStyles = createUseStyles({
    ionContent: {
        '--padding-top': '0',
        '--padding-bottom': '0',
        height: '100%',
        display: 'flex'
    },

    container: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        background: 'linear-gradient(135deg, #11789D 0%, #923DAC 100%)',
        overflow: 'hidden',
        transition: 'background 1.2s ease-in-out'
    },

    containerMoonPhase: {
        background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)'
    },

    closeButton: {
        position: 'absolute',
        top: 'calc(var(--ion-safe-area-top, 0px) + 20px)',
        right: '20px',
        width: '40px',
        height: '40px',
        borderRadius: '50%',
        border: 'none',
        background: 'rgba(255,255,255,0.2)',
        color: 'white',
        fontSize: '24px',
        cursor: 'pointer',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        transition: 'background 0.2s',
        zIndex: 10,

        '&:hover': {
            background: 'rgba(255,255,255,0.3)'
        }
    },

    content: {
        position: 'relative',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        padding: '40px'
    },

    // ===== CELEBRATION PHASE =====
    celebrationContent: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 1,
        transition: 'all 0.5s ease-out 0.3s',

        '&.hidden': {
            opacity: 0,
            pointerEvents: 'none'
        }
    },

    circle: {
        position: 'relative',
        width: '140px',
        height: '140px',
        marginBottom: '40px'
    },

    circleSvg: {
        position: 'absolute',
        width: '140px',
        height: '140px',
        transform: 'rotate(-90deg)'
    },

    backgroundCircle: {
        fill: 'none',
        stroke: 'rgba(255,255,255,0.2)',
        strokeWidth: '4'
    },

    animatedCircle: {
        fill: 'none',
        stroke: 'white',
        strokeWidth: '4',
        strokeLinecap: 'round',
        strokeDasharray: '377',
        strokeDashoffset: '377',
        animation: '$drawCircle 1.5s ease-out forwards'
    },

    checkmark: {
        position: 'absolute',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        opacity: 0,
        animation: '$checkAppear 0.4s ease-out 1.2s forwards'
    },

    checkmarkSvg: {
        width: '70px',
        height: '70px'
    },

    checkmarkPath: {
        stroke: 'white',
        strokeWidth: '3',
        strokeLinecap: 'round',
        strokeLinejoin: 'round',
        fill: 'none',
        strokeDasharray: '50',
        strokeDashoffset: '50',
        animation: '$checkDraw 0.4s ease-out 1.2s forwards'
    },

    pulseRing: {
        position: 'absolute',
        width: '140px',
        height: '140px',
        borderRadius: '50%',
        border: '3px solid rgba(255,255,255,0.6)',
        top: 0,
        left: 0,
        animation: '$pulseRing 1s ease-out 1.5s'
    },

    celebrationTitle: {
        color: 'white',
        fontSize: '42px',
        fontWeight: '700',
        margin: '0',
        textShadow: '0 2px 10px rgba(0,0,0,0.2)'
    },

    celebrationMessage: {
        color: 'rgba(255,255,255,0.95)',
        fontSize: '16px',
        lineHeight: '1.6',
        margin: '8px 0 0 0',
        textShadow: '0 1px 5px rgba(0,0,0,0.2)',
        maxWidth: '280px'
    },

    // ===== MOON PHASE =====
    moonContent: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'center',
        justifyContent: 'center',
        opacity: 0,
        transition: 'opacity 0.8s ease-out 0.2s',

        '&.visible': {
            opacity: 1
        }
    },

    starsContainer: {
        position: 'absolute',
        width: '100%',
        height: '100%',
        top: 0,
        left: 0,
        zIndex: 0,
        pointerEvents: 'none'
    },

    moonGlow: {
        position: 'absolute',
        width: '200px',
        height: '200px',
        borderRadius: '50%',
        background: 'radial-gradient(circle, rgba(240,230,140,0.15) 0%, transparent 70%)',
        top: '50%',
        left: '50%',
        transform: 'translate(-50%, -50%)',
        animation: '$gentlePulse 5s ease-in-out infinite'
    },

    moonBody: {
        position: 'relative',
        width: '120px',
        height: '120px',
        marginBottom: '40px',
        animation: '$moonFadeIn 0.8s ease-out forwards'
    },

    moonCircle: {
        position: 'absolute',
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        background: 'linear-gradient(135deg, #FFFACD 0%, #F0E68C 100%)',
        boxShadow: '0 0 35px rgba(240, 230, 140, 0.5)',
        left: 0,
        top: 0
    },

    moonShadow: {
        position: 'absolute',
        width: '90px',
        height: '90px',
        borderRadius: '50%',
        background: 'linear-gradient(to bottom, #1a1a2e 0%, #16213e 50%, #0f3460 100%)',
        left: '20px',
        top: 0
    },

    moonCrater: {
        position: 'absolute',
        width: '8px',
        height: '8px',
        borderRadius: '50%',
        background: 'rgba(0,0,0,0.1)',
        zIndex: 1,

        '&:nth-child(3)': {
            top: '35px',
            left: '12px'
        },

        '&:nth-child(4)': {
            top: '55px',
            left: '40px'
        }
    },

    moonTitle: {
        color: 'white',
        fontSize: '36px',
        fontWeight: '700',
        margin: '0',
        textShadow: '0 2px 10px rgba(0,0,0,0.3)'
    },

    moonMessage: {
        color: 'rgba(255,255,255,0.9)',
        fontSize: '16px',
        lineHeight: '1.6',
        margin: '8px 0 0 0',
        textShadow: '0 1px 5px rgba(0,0,0,0.3)',
        maxWidth: '280px'
    },

    // ===== ANIMATIONS =====
    '@keyframes drawCircle': {
        to: { strokeDashoffset: 0 }
    },

    '@keyframes checkAppear': {
        to: { opacity: 1 }
    },

    '@keyframes checkDraw': {
        to: { strokeDashoffset: 0 }
    },

    '@keyframes pulseRing': {
        '0%': { transform: 'scale(1)', opacity: 0.6 },
        '100%': { transform: 'scale(1.3)', opacity: 0 }
    },

    '@keyframes starAppear': {
        to: { opacity: 1 }
    },

    '@keyframes twinkle': {
        '0%, 100%': { opacity: 1, transform: 'scale(1)' },
        '50%': { opacity: 0.3, transform: 'scale(0.8)' }
    },

    '@keyframes gentlePulse': {
        '0%, 100%': { transform: 'translate(-50%, -50%) scale(1)', opacity: 0.15 },
        '50%': { transform: 'translate(-50%, -50%) scale(1.08)', opacity: 0.22 }
    },

    '@keyframes moonFadeIn': {
        '0%': { opacity: 0, transform: 'scale(0.8)' },
        '100%': { opacity: 1, transform: 'scale(1)' }
    }
});

interface CelebrationModalProps {
    isOpen: boolean;
    onClose: () => void;
    autoCloseDuration?: number;
}

export const CelebrationModal: React.FC<CelebrationModalProps> = ({
    isOpen,
    onClose,
    autoCloseDuration = 6000
}) => {
    const classes = useStyles();
    const [phase, setPhase] = useState<'celebration' | 'moon'>('celebration');

    useEffect(() => {
        if (!isOpen) {
            setPhase('celebration');
            return;
        }

        // Move to moon phase after 2 seconds
        const moonTimer = setTimeout(() => {
            setPhase('moon');
        }, 2000);

        // Auto-close after duration
        const closeTimer = setTimeout(() => {
            onClose();
        }, autoCloseDuration);

        return () => {
            clearTimeout(moonTimer);
            clearTimeout(closeTimer);
        };
    }, [isOpen, autoCloseDuration, onClose]);

    // Generate stars distributed around the edges of the screen
    const stars = Array.from({ length: 12 }, (_, i) => {
        const angle = (i / 12) * Math.PI * 2;
        const radius = 35 + Math.random() * 15;
        const x = 50 + Math.cos(angle) * radius;
        const y = 50 + Math.sin(angle) * radius;
        return {
            id: i,
            x: Math.max(5, Math.min(95, x)),
            y: Math.max(5, Math.min(95, y)),
            size: 1 + Math.random() * 2,
            delay: Math.random() * 1.5
        };
    });

    return (
        <IonModal
            isOpen={isOpen}
            onDidDismiss={onClose}
            backdropDismiss={false}
            data-id="celebration-modal"
        >
            <style>
                {`
                    @keyframes starAppear {
                        to { opacity: 1; }
                    }
                    @keyframes twinkle {
                        0%, 100% { opacity: 1; transform: scale(1); }
                        50% { opacity: 0.3; transform: scale(0.8); }
                    }
                `}
            </style>
            <IonContent className={classes.ionContent}>
                <div className={`${classes.container} ${phase === 'moon' ? classes.containerMoonPhase : ''}`}>
                    {/* Close button */}
                    <button
                        className={classes.closeButton}
                        onClick={onClose}
                        aria-label="Close celebration"
                        data-id="celebration-close-button"
                    >
                        ✕
                    </button>

                    <div className={classes.content}>
                        {/* CELEBRATION PHASE */}
                        <div className={`${classes.celebrationContent} ${phase === 'moon' ? 'hidden' : ''}`}>
                            <div className={classes.circle}>
                                <svg className={classes.circleSvg} viewBox="0 0 140 140">
                                    <circle className={classes.backgroundCircle} cx="70" cy="70" r="60" />
                                    <circle className={classes.animatedCircle} cx="70" cy="70" r="60" />
                                </svg>

                                <div className={classes.checkmark}>
                                    <svg className={classes.checkmarkSvg} viewBox="0 0 24 24">
                                        <path className={classes.checkmarkPath} d="M5 13l4 4L19 7" />
                                    </svg>
                                </div>

                                <div className={classes.pulseRing} />
                            </div>

                            <h1 className={classes.celebrationTitle}>All Done!</h1>
                        </div>

                        {/* MOON PHASE */}
                        <div className={`${classes.moonContent} ${phase === 'moon' ? 'visible' : ''}`}>
                            {/* Stars */}
                            <div className={classes.starsContainer} data-id="stars-container">
                                {stars.map((star) => (
                                    <div
                                        key={star.id}
                                        style={{
                                            position: 'absolute',
                                            width: `${star.size}px`,
                                            height: `${star.size}px`,
                                            borderRadius: '50%',
                                            background: 'white',
                                            left: `${star.x}%`,
                                            top: `${star.y}%`,
                                            boxShadow: '0 0 8px rgba(255,255,255,0.8)',
                                            opacity: 0,
                                            animation: `starAppear 0.8s ease-out forwards, twinkle 2s ease-in-out infinite`,
                                            animationDelay: `${star.delay}s, ${star.delay + 0.8}s`
                                        }}
                                        data-id={`star-${star.id}`}
                                    />
                                ))}
                            </div>

                            {/* Glow */}
                            <div className={classes.moonGlow} />

                            {/* Moon */}
                            <div className={classes.moonBody}>
                                <div className={classes.moonCircle} />
                                <div className={classes.moonShadow} />
                                <div className={classes.moonCrater} />
                                <div className={classes.moonCrater} />
                            </div>

                            <h1 className={classes.moonTitle}>Rest Easy!</h1>
                            <p className={classes.moonMessage}>
                                Everything's taken care of for tonight. Time to unwind—you've earned it!
                            </p>
                        </div>
                    </div>
                </div>
            </IonContent>
        </IonModal>
    );
};
